import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, LayoutDashboard, Server, RefreshCw, BarChart3, Siren,
  Scale, Beaker, Lock, FileText, Play, RotateCcw, Copy, CheckCircle2,
} from "lucide-react";
import { endpoints } from "@/lib/api";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "booths", label: "Booth Registry", icon: Server },
  { key: "sync", label: "Sync & Chain", icon: RefreshCw },
  { key: "results", label: "Results", icon: BarChart3 },
  { key: "reconciliation", label: "Reconciliation", icon: Scale },
  { key: "incidents", label: "Incidents", icon: Siren },
  { key: "adversarial", label: "Adversarial", icon: Beaker },
];

export default function AdminPortal() {
  const { tab } = useParams();
  const nav = useNavigate();
  const active = tab || "overview";

  return (
    <div className="min-h-screen bg-white text-zinc-900 flex">
      {/* Sidebar */}
      <aside className="w-64 border-r border-zinc-200 flex flex-col shrink-0 min-h-screen">
        <div className="tricolor-strip h-1" />
        <div className="p-5 border-b border-zinc-200 flex items-center gap-3">
          <Link to="/" data-testid="admin-home" className="text-zinc-500 hover:text-zinc-900"><ArrowLeft className="w-4 h-4" /></Link>
          <div className="w-8 h-8 border border-zinc-900 grid place-items-center"><Lock className="w-3.5 h-3.5" /></div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">ECI Portal</div>
            <div className="font-display font-black text-sm leading-none">OPERATIONS</div>
          </div>
        </div>
        <nav className="flex-1 py-3">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => nav(`/admin/${t.key}`)}
              className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm text-left border-l-2 transition-colors ${active === t.key ? "border-[#0A369D] bg-zinc-50 text-zinc-900 font-medium" : "border-transparent text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50"}`}
            >
              <t.icon className="w-4 h-4" strokeWidth={2} />{t.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-zinc-200 text-[10px] font-mono text-zinc-500">Anandpur SAC By-Election 2026 · PROTOTYPE</div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0">
        <div className="border-b border-zinc-200 px-8 py-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">ECI Admin</div>
            <div className="font-display font-black text-2xl">{TABS.find((t) => t.key === active)?.label}</div>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono text-zinc-600">
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-[#138808] rounded-full" /> LIVE · IST {new Date().toLocaleTimeString()}</span>
          </div>
        </div>
        <div className="p-8">
          {active === "overview" && <Overview />}
          {active === "booths" && <Booths />}
          {active === "sync" && <SyncChain />}
          {active === "results" && <Results />}
          {active === "reconciliation" && <Reconciliation />}
          {active === "incidents" && <Incidents />}
          {active === "adversarial" && <Adversarial />}
        </div>
      </main>
    </div>
  );
}

function useAsync(fn, deps = []) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  useEffect(() => {
    let mounted = true;
    setState((s) => ({ ...s, loading: true }));
    fn()
      .then((data) => mounted && setState({ loading: false, data, error: null }))
      .catch((error) => mounted && setState({ loading: false, data: null, error }));
    return () => { mounted = false; };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

function Overview() {
  const status = useAsync(() => endpoints.syncStatus());
  const results = useAsync(() => endpoints.results());
  const incidents = useAsync(() => endpoints.incidents());

  const totals = results.data?.electronic_totals || {};
  const totalBallots = Object.values(totals).reduce((a, b) => a + b, 0);
  const openIncidents = (incidents.data || []).filter((i) => i.status === "open").length;
  const openedBooths = (status.data || []).filter((b) => b.status !== "provisioned").length;

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-4 gap-3">
        <KPI testid="kpi-booths" label="Booths under mgmt" value={(status.data || []).length} />
        <KPI testid="kpi-open-booths" label="Opened / sealed" value={openedBooths} />
        <KPI testid="kpi-ballots" label="Ballots aggregated" value={totalBallots} tone="ok" />
        <KPI testid="kpi-incidents" label="Open incidents" value={openIncidents} tone={openIncidents > 0 ? "warn" : "default"} />
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <Panel title="Live Booth Feed">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
              <tr><th className="text-left py-2">Booth</th><th className="text-right">Events</th><th className="text-right">Ballots</th><th className="text-right">Status</th></tr>
            </thead>
            <tbody>
              {(status.data || []).map((b) => (
                <tr key={b.booth_id} className="border-b border-zinc-100">
                  <td className="py-2"><span className="font-mono text-xs">{b.booth_id}</span> <span className="text-zinc-500 text-xs"> — {b.name}</span></td>
                  <td className="text-right font-mono">{b.event_count}</td>
                  <td className="text-right font-mono">{b.ballot_count}</td>
                  <td className="text-right"><StatusChip status={b.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
        <Panel title="Recent Incidents">
          {(incidents.data || []).slice(0, 6).length === 0 && <Empty text="No incidents reported yet." />}
          {(incidents.data || []).slice(0, 6).map((i) => (
            <div key={i.id} className="border-b border-zinc-100 py-3">
              <div className="flex items-center justify-between">
                <div className="font-mono text-xs text-zinc-500">{i.booth_id} · {new Date(i.created_at).toLocaleString()}</div>
                <SeverityChip s={i.severity} />
              </div>
              <div className="text-sm mt-0.5">{i.kind}</div>
              <div className="text-xs text-zinc-500">{i.description}</div>
            </div>
          ))}
        </Panel>
      </div>
    </div>
  );
}

function Booths() {
  const status = useAsync(() => endpoints.syncStatus());
  const [copied, setCopied] = useState(null);
  return (
    <Panel title="Registered Booths & Device Certificates">
      <table className="w-full text-sm" data-testid="booths-table">
        <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
          <tr><th className="text-left py-2">Booth</th><th className="text-left">Constituency</th><th className="text-left">Serial</th><th className="text-left">Certificate</th><th className="text-right">Status</th></tr>
        </thead>
        <tbody>
          {(status.data || []).map((b) => (
            <tr key={b.booth_id} className="border-b border-zinc-100">
              <td className="py-3"><div className="font-mono text-xs">{b.booth_id}</div><div className="text-xs text-zinc-500">{b.name}</div></td>
              <td className="text-xs text-zinc-600">{b.constituency}</td>
              <td className="font-mono text-xs">{b.device_serial}</td>
              <td>
                <button
                  onClick={() => { navigator.clipboard.writeText(b.device_cert_fingerprint); setCopied(b.booth_id); toast.success("Certificate fingerprint copied"); }}
                  className="hash-chip inline-flex items-center gap-1 hover:border-zinc-900"
                  data-testid={`cert-${b.booth_id}`}
                >
                  {b.device_cert_fingerprint.slice(0, 22)}… <Copy className="w-3 h-3" />
                </button>
              </td>
              <td className="text-right"><StatusChip status={b.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

function SyncChain() {
  const events = useAsync(() => endpoints.allEvents());
  return (
    <Panel title="Hash-chained Event Log · append-only">
      <div className="max-h-[70vh] overflow-auto">
        <table className="w-full text-xs font-mono">
          <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200 sticky top-0 bg-white">
            <tr><th className="text-left py-2 px-2">seq</th><th className="text-left">time</th><th className="text-left">booth</th><th className="text-left">kind</th><th className="text-left">prev → hash</th><th className="text-right">ok</th></tr>
          </thead>
          <tbody>
            {(events.data || []).map((e, i) => (
              <tr key={e.event_id} className={i % 2 ? "bg-white" : "bg-zinc-50"}>
                <td className="px-2 py-1.5 text-zinc-500">{e.seq ?? "-"}</td>
                <td className="text-zinc-600">{new Date(e.timestamp).toLocaleTimeString()}</td>
                <td>{e.booth_id}</td>
                <td className="text-zinc-900">{e.kind}</td>
                <td className="text-zinc-500">{e.prev_hash.slice(0, 8)} → <span className="text-zinc-900">{e.hash.slice(0, 12)}</span></td>
                <td className="text-right">{e.chain_ok === false ? <span className="text-[#DC2626]">FAIL</span> : <CheckCircle2 className="w-3 h-3 text-[#138808] inline" />}</td>
              </tr>
            ))}
            {(events.data || []).length === 0 && <tr><td colSpan={6}><Empty text="No events synchronised yet. Cast a vote on the Polling Kiosk and press Push Sync." /></td></tr>}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function Results() {
  const results = useAsync(() => endpoints.results());
  const r = results.data;
  if (!r) return <Empty text="Loading tallies…" />;
  const total = Object.values(r.electronic_totals).reduce((a, b) => a + b, 0) || 1;
  return (
    <div className="grid md:grid-cols-[1fr,320px] gap-6">
      <Panel title="Electronic Totals · aggregated across booths">
        <div className="space-y-4" data-testid="results-list">
          {r.candidates.map((c) => {
            const n = r.electronic_totals[c.id] || 0;
            const pct = Math.round((n / total) * 100);
            return (
              <div key={c.id}>
                <div className="flex items-baseline justify-between mb-1">
                  <div>
                    <span className="font-display font-bold">{c.name}</span>
                    <span className="text-xs font-mono text-zinc-500 ml-2">{c.party}</span>
                  </div>
                  <div className="font-mono text-sm"><span className="font-bold">{n}</span> <span className="text-zinc-500">({pct}%)</span></div>
                </div>
                <div className="h-2 border border-zinc-200 relative">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6 }} style={{ background: c.color, height: "100%" }} />
                </div>
              </div>
            );
          })}
        </div>
      </Panel>
      <Panel title="Per-Booth Split">
        <table className="w-full text-xs font-mono">
          <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
            <tr><th className="text-left py-2">Booth</th>{r.candidates.map((c) => <th key={c.id} className="text-right">{c.id}</th>)}</tr>
          </thead>
          <tbody>
            {Object.entries(r.per_booth).map(([bid, row]) => (
              <tr key={bid} className="border-b border-zinc-100">
                <td className="py-1.5">{bid}</td>
                {r.candidates.map((c) => <td key={c.id} className="text-right">{row[c.id] || 0}</td>)}
              </tr>
            ))}
            {Object.keys(r.per_booth).length === 0 && <tr><td colSpan={r.candidates.length + 1}><Empty text="Waiting for booth sync." /></td></tr>}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

function Reconciliation() {
  const results = useAsync(() => endpoints.results());
  const auditCases = useAsync(() => endpoints.auditCases());
  const r = results.data;
  async function openCase(booth_id, reason) {
    await endpoints.openAuditCase({ booth_id, reason });
    toast.success("Audit case opened · chain of custody frozen");
    window.location.reload();
  }
  if (!r) return <Empty text="Loading reconciliation…" />;
  const mismatches = r.candidates.filter((c) => (r.electronic_totals[c.id] || 0) !== (r.vvpat_totals[c.id] || 0));
  return (
    <div className="space-y-6">
      <Panel title="Electronic vs VVPAT · candidate-level">
        <table className="w-full text-sm" data-testid="recon-table">
          <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
            <tr><th className="text-left py-2">Candidate</th><th className="text-right">Electronic</th><th className="text-right">VVPAT</th><th className="text-right">Δ</th><th className="text-right">Verdict</th></tr>
          </thead>
          <tbody>
            {r.candidates.map((c) => {
              const e = r.electronic_totals[c.id] || 0;
              const v = r.vvpat_totals[c.id] || 0;
              const delta = e - v;
              return (
                <tr key={c.id} className="border-b border-zinc-100">
                  <td className="py-2"><span className="font-display font-bold">{c.name}</span> <span className="text-zinc-500 text-xs">· {c.party}</span></td>
                  <td className="text-right font-mono">{e}</td>
                  <td className="text-right font-mono">{v}</td>
                  <td className={`text-right font-mono ${delta !== 0 ? "text-[#DC2626]" : ""}`}>{delta}</td>
                  <td className="text-right">{delta === 0 ? <span className="text-[10px] font-mono text-[#138808]">MATCH</span> : <span className="text-[10px] font-mono text-[#DC2626]">MISMATCH</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {mismatches.length > 0 && (
          <div className="mt-4 border border-[#EA580C] p-4 flex items-center justify-between">
            <div>
              <div className="font-display font-bold">Discrepancy detected</div>
              <div className="text-xs text-zinc-600">Freeze evidence and open a formal audit case. Recount decisions rest with the Returning Officer under prescribed rules — the system never triggers automatic re-election.</div>
            </div>
            <button data-testid="btn-open-audit" onClick={() => openCase("BTH-042", "electronic-vs-vvpat mismatch")} className="bg-[#0A369D] text-white px-4 py-2">Open Audit Case</button>
          </div>
        )}
      </Panel>
      <Panel title="Open Audit Cases">
        {(auditCases.data || []).length === 0 && <Empty text="No audit cases open." />}
        {(auditCases.data || []).map((c) => (
          <div key={c.id} className="border-b border-zinc-100 py-3">
            <div className="flex justify-between items-baseline">
              <div><span className="font-display font-bold">{c.reason}</span> <span className="text-xs font-mono text-zinc-500 ml-2">{c.booth_id}</span></div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#EA580C]">{c.status}</div>
            </div>
            <ol className="mt-2 text-xs font-mono text-zinc-600 list-decimal ml-4 space-y-0.5">
              {c.steps.map((s, i) => <li key={i}>{s.step}</li>)}
            </ol>
          </div>
        ))}
      </Panel>
    </div>
  );
}

function Incidents() {
  const list = useAsync(() => endpoints.incidents());
  return (
    <Panel title="Incident Register">
      <table className="w-full text-sm" data-testid="incidents-table">
        <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
          <tr><th className="text-left py-2">When</th><th className="text-left">Booth</th><th className="text-left">Kind</th><th className="text-left">Severity</th><th className="text-left">Reporter</th><th className="text-left">Description</th></tr>
        </thead>
        <tbody>
          {(list.data || []).map((i) => (
            <tr key={i.id} className="border-b border-zinc-100">
              <td className="py-2 text-xs font-mono text-zinc-500">{new Date(i.created_at).toLocaleString()}</td>
              <td className="text-xs font-mono">{i.booth_id}</td>
              <td className="text-sm">{i.kind}</td>
              <td><SeverityChip s={i.severity} /></td>
              <td className="text-xs">{i.reported_by}</td>
              <td className="text-xs text-zinc-600">{i.description}</td>
            </tr>
          ))}
          {(list.data || []).length === 0 && <tr><td colSpan={6}><Empty text="No incidents yet." /></td></tr>}
        </tbody>
      </table>
    </Panel>
  );
}

function Adversarial() {
  const scenarios = [
    { key: "vvpat_jam", label: "VVPAT paper jam", desc: "Printer jams mid-slip. Ballot is voided; voter re-authorised under fallback rules." },
    { key: "tamper_seal", label: "Tamper-seal broken", desc: "Enclosure micro-switch fires. Device enters read-only, sync forced, critical incident opened." },
    { key: "count_mismatch", label: "Electronic vs VVPAT mismatch", desc: "Injects a −2 slip discrepancy on candidate C1 to demonstrate the reconciliation path." },
    { key: "duplicate_sync", label: "Duplicate sync attempt", desc: "Re-uploads a previously synced event; the API deduplicates by event_id." },
    { key: "clock_skew", label: "Clock manipulation", desc: "Simulates a large local clock skew; log timestamps are flagged during audit." },
    { key: "firmware_alarm", label: "Unsigned firmware alarm", desc: "Secure-boot signature mismatch. Device refuses to open and reports upstream." },
  ];
  async function inject(scenario) {
    await endpoints.injectTamper({ booth_id: "BTH-042", scenario });
    toast.warning(`Injected: ${scenario.replace("_", " ")}`);
  }
  async function reset() {
    await endpoints.reset();
    toast.success("Prototype reset · booths, ballots, incidents cleared");
  }
  return (
    <div className="space-y-6">
      <div className="border border-[#EA580C] p-4 flex items-start gap-3">
        <Beaker className="w-4 h-4 text-[#EA580C] mt-0.5" />
        <div className="text-xs text-zinc-700 leading-relaxed">Adversarial injections are for prototype/demo only. In a real deployment these would be triggered by hardware sensors, cryptographic checks, and independent audit staff — never a portal button.</div>
      </div>
      <div className="grid md:grid-cols-2 gap-3" data-testid="adversarial-grid">
        {scenarios.map((s) => (
          <div key={s.key} className="border border-zinc-200 p-5">
            <div className="flex items-center justify-between mb-1">
              <div className="font-display font-bold">{s.label}</div>
              <button data-testid={`inject-${s.key}`} onClick={() => inject(s.key)} className="inline-flex items-center gap-1.5 bg-zinc-900 text-white text-xs px-3 py-1.5 hover:bg-zinc-800"><Play className="w-3 h-3" />Inject</button>
            </div>
            <div className="text-xs text-zinc-600 leading-relaxed">{s.desc}</div>
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <button onClick={reset} data-testid="btn-reset-all" className="inline-flex items-center gap-2 border border-[#DC2626] text-[#DC2626] px-4 py-2 hover:bg-[#fdecec]"><RotateCcw className="w-4 h-4" /> Reset entire prototype</button>
      </div>
    </div>
  );
}

// -------------- shared UI --------------
function Panel({ title, children }) {
  return (
    <section className="border border-zinc-200 bg-white">
      <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
        <div className="font-display font-bold text-sm">{title}</div>
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}
function KPI({ label, value, tone = "default", testid }) {
  const tones = { default: "text-zinc-900", ok: "text-[#138808]", warn: "text-[#EA580C]" };
  return (
    <div className="border border-zinc-200 p-4" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display font-black text-4xl ${tones[tone]}`}>{value}</div>
    </div>
  );
}
function StatusChip({ status }) {
  const map = {
    provisioned: ["ready to open", "#52525B"],
    opened: ["polling", "#0A369D"],
    closed: ["closed", "#EA580C"],
    sealed: ["sealed", "#138808"],
  };
  const [label, color] = map[status] || [status, "#52525B"];
  return <span className="inline-flex items-center px-2 py-0.5 border text-[10px] font-mono uppercase tracking-widest" style={{ borderColor: color, color }}>{label}</span>;
}
function SeverityChip({ s }) {
  const color = { low: "#52525B", medium: "#0A369D", high: "#EA580C", critical: "#DC2626" }[s] || "#52525B";
  return <span className="inline-flex px-2 py-0.5 border text-[10px] font-mono uppercase tracking-widest" style={{ borderColor: color, color }}>{s}</span>;
}
function Empty({ text }) { return <div className="text-sm text-zinc-500 py-10 text-center">{text}</div>; }
